<script setup>
import { onMounted, ref, watch } from 'vue'
import { useAgentAdminStore } from '@/stores/agentAdmin.store'

// RM-208: standalone AI Annotations list — every annotation across all
// conversations, filterable by tracking status. Kapil marks statuses here (or
// in the conversation viewer); Claude Code reads the same records via the API.

const store = useAgentAdminStore()

const statusFilter = ref('open')
const currentPage = ref(1)
const itemsPerPage = 25

const STATUS_FILTERS = [
  { title: 'Needs to be fixed', value: 'open' },
  { title: 'In progress', value: 'in_progress' },
  { title: 'Complete', value: 'verified' },
  { title: "Won't fix", value: 'wont_fix' },
  { title: 'All', value: '' },
]

const STATUS_ITEMS = STATUS_FILTERS.filter(s => s.value)

const CATEGORY_LABELS = {
  prompt: 'Prompt instructions',
  routing: 'Intent routing',
  retrieval_data: 'RM data / retrieval',
  tool: 'Tool behavior',
  conversation_state: 'Conversation state',
  workflow_product: 'Workflow / product gap',
  ui: 'UI',
  performance: 'Performance',
  bug: 'Code bug',
  copy_only: 'Copy only',
  unknown: 'Unknown',
}

const statusSaving = ref({})

// Inline editing: refine any annotation (esp. member 👎s, which arrive with a
// generic "should have happened") with your own diagnosis, category, severity,
// and working notes — the PATCH endpoint has supported these all along.
const editingId = ref(null)
const editForm = ref({})
const editSaving = ref(false)
const expandedId = ref(null)
const contextId = ref(null)

const CATEGORY_ITEMS = Object.entries(CATEGORY_LABELS).map(([value, title]) => ({ title, value }))
const SEVERITY_ITEMS = [
  { title: 'Low', value: 'low' },
  { title: 'Medium', value: 'medium' },
  { title: 'High', value: 'high' },
]

function startEdit(f) {
  editingId.value = f._id
  editForm.value = {
    expectedBehavior: f.expectedBehavior || '',
    adminNote: f.adminNote || '',
    category: f.category || 'unknown',
    severity: f.severity || 'medium',
    resolutionSummary: f.resolutionSummary || '',
  }
}

function cancelEdit() {
  editingId.value = null
  editForm.value = {}
}

async function saveEdit(f) {
  if (editSaving.value) return
  editSaving.value = true
  try {
    await store.updateFeedback(f._id, {
      expectedBehavior: editForm.value.expectedBehavior?.trim() || null,
      adminNote: editForm.value.adminNote?.trim() || null,
      category: editForm.value.category,
      severity: editForm.value.severity,
      resolutionSummary: editForm.value.resolutionSummary?.trim() || null,
    })
    cancelEdit()
  } finally {
    editSaving.value = false
  }
}

// Display helper: show markdown link labels, not raw [label](url) syntax.
const displayText = text => (text || '').replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')

// Full-conversation dialog: the complete live thread, in place — no hopping to
// the AI Conversations screen and back. The annotated reply is highlighted.
// Falls back to the durable snapshot when the conversation has expired (TTL).
const convDialogOpen = ref(false)
const convDialogFeedback = ref(null)

async function openConversation(f) {
  convDialogFeedback.value = f
  convDialogOpen.value = true
  store.clearActiveConversation()
  await store.getConversation(f.conversationId)
}

function closeConversation() {
  convDialogOpen.value = false
  convDialogFeedback.value = null
  store.clearActiveConversation()
}

const loadData = async () => {
  await store.getFeedbackList({
    page: currentPage.value,
    limit: itemsPerPage,
    status: statusFilter.value || undefined,
  })
}

watch(statusFilter, () => {
  currentPage.value = 1
  loadData()
})

const onPageChange = newPage => {
  currentPage.value = newPage
  loadData()
}

async function updateStatus(feedback, newStatus) {
  if (!newStatus || newStatus === feedback.status) return
  statusSaving.value = { ...statusSaving.value, [feedback._id]: true }
  try {
    await store.updateFeedback(feedback._id, { status: newStatus })
  } finally {
    statusSaving.value = { ...statusSaving.value, [feedback._id]: false }
  }
}

const statusColor = status => {
  if (status === 'verified') return 'success'
  if (status === 'wont_fix' || status === 'duplicate') return 'medium-emphasis'
  if (status === 'in_progress' || status === 'ready_for_verification') return 'info'

  return 'warning'
}

const severityColor = severity => {
  if (severity === 'high') return 'error'
  if (severity === 'medium') return 'warning'

  return 'medium-emphasis'
}

const formatDateTime = dateStr => {
  if (!dateStr) return ''
  const d = new Date(dateStr)

  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

onMounted(loadData)
</script>

<template>
  <div class="content-wrapper mt-6">
    <VCard class="pa-6 pt-5">
      <VRow
        align="center"
        class="mb-2"
      >
        <VCol>
          <h3 class="user-dashboard-card-title mb-0">
            AI Annotations
          </h3>
          <div class="text-caption text-medium-emphasis">
            Feedback on AI replies, annotated from AI Conversations. {{ store.feedbackTotal }} total in this view.
          </div>
        </VCol>
        <VCol cols="auto">
          <VSelect
            v-model="statusFilter"
            :items="STATUS_FILTERS"
            label="Status"
            density="compact"
            hide-details
            style="min-width: 190px"
            class="annotations__input"
          />
        </VCol>
      </VRow>

      <!-- Loading -->
      <div
        v-if="store.feedbackLoading"
        class="text-center py-8"
      >
        <VProgressCircular
          indeterminate
          color="primary"
        />
      </div>

      <!-- Empty -->
      <div
        v-else-if="!store.feedbackList.length"
        class="text-center py-8 text-medium-emphasis"
      >
        No annotations here. Open AI Conversations and use the annotate control under any AI reply to add one.
      </div>

      <!-- List -->
      <div v-else>
        <VCard
          v-for="f in store.feedbackList"
          :key="f._id"
          variant="outlined"
          class="pa-4 mb-3"
        >
          <div class="d-flex flex-wrap align-center ga-2 mb-2">
            <VSelect
              :model-value="f.status"
              :items="STATUS_ITEMS"
              :color="statusColor(f.status)"
              :base-color="statusColor(f.status)"
              :loading="statusSaving[f._id]"
              density="compact"
              variant="outlined"
              hide-details
              style="width: 215px; flex: 0 0 auto"
              class="annotations__input"
              @update:model-value="val => updateStatus(f, val)"
            />
            <VChip
              v-if="f.source === 'member'"
              size="small"
              color="info"
              variant="tonal"
            >
              Member {{ f.memberRating === 'up' ? '👍' : '👎' }}
            </VChip>
            <VChip
              v-if="f.strongExample"
              size="small"
              color="success"
              variant="tonal"
            >
              Strong example
            </VChip>
            <template v-else>
              <VChip
                size="small"
                variant="tonal"
              >
                {{ CATEGORY_LABELS[f.category] || f.category }}
              </VChip>
              <VChip
                size="small"
                :color="severityColor(f.severity)"
                variant="tonal"
              >
                {{ f.severity }}
              </VChip>
            </template>
            <VSpacer />
            <span class="text-caption text-medium-emphasis">{{ formatDateTime(f.createdAt) }}</span>
          </div>

          <div
            v-if="f.memberText"
            class="text-body-2 mb-1"
          >
            <strong>Member:</strong> {{ displayText(f.memberText) }}
          </div>
          <div
            v-if="f.aiText"
            class="text-body-2 text-medium-emphasis mb-2"
            :class="{ 'annotations__ai-text': expandedId !== f._id }"
          >
            <strong>AI:</strong> {{ displayText(f.aiText) }}
          </div>

          <!-- Read view -->
          <template v-if="editingId !== f._id">
            <div
              v-if="f.expectedBehavior"
              class="text-body-2 mb-1"
            >
              <strong>Should have happened:</strong> {{ f.expectedBehavior }}
            </div>
            <div
              v-if="f.adminNote"
              class="text-body-2 mb-1"
            >
              <strong>Note:</strong> {{ f.adminNote }}
            </div>
            <div
              v-if="f.resolutionSummary || f.resolutionRef"
              class="text-body-2 mb-1"
            >
              <strong>Resolution:</strong> {{ f.resolutionSummary || '' }}
              <span v-if="f.resolutionRef"> ({{ f.resolutionRef }})</span>
            </div>
          </template>

          <!-- Edit view -->
          <div
            v-else
            class="mt-2"
          >
            <VTextarea
              v-model="editForm.expectedBehavior"
              label="What should have happened?"
              rows="2"
              auto-grow
              class="mb-2 annotations__input"
            />
            <VTextarea
              v-model="editForm.adminNote"
              label="Notes / what was wrong (optional)"
              rows="2"
              auto-grow
              class="mb-2 annotations__input"
            />
            <VTextarea
              v-model="editForm.resolutionSummary"
              label="Resolution notes (optional)"
              rows="1"
              auto-grow
              class="mb-2 annotations__input"
            />
            <div class="d-flex flex-wrap ga-3 mb-2">
              <VSelect
                v-model="editForm.category"
                :items="CATEGORY_ITEMS"
                label="Likely cause"
                density="comfortable"
                class="annotations__input"
                style="min-width: 220px"
              />
              <VSelect
                v-model="editForm.severity"
                :items="SEVERITY_ITEMS"
                label="Severity"
                density="comfortable"
                class="annotations__input"
                style="max-width: 160px"
              />
            </div>
            <div class="d-flex ga-2">
              <VBtn
                size="small"
                color="primary"
                variant="flat"
                :loading="editSaving"
                @click="saveEdit(f)"
              >
                Save
              </VBtn>
              <VBtn
                size="small"
                variant="text"
                @click="cancelEdit"
              >
                Cancel
              </VBtn>
            </div>
          </div>

          <div class="d-flex align-center ga-3 mt-1">
            <a
              class="text-caption annotations__action"
              @click.prevent="openConversation(f)"
            >
              View full conversation
            </a>
            <a
              v-if="editingId !== f._id"
              class="text-caption annotations__action"
              @click.prevent="startEdit(f)"
            >
              Edit / add comments
            </a>
            <a
              v-if="f.aiText && f.aiText.length > 200"
              class="text-caption annotations__action"
              @click.prevent="expandedId = expandedId === f._id ? null : f._id"
            >
              {{ expandedId === f._id ? 'Show less' : 'Show full reply' }}
            </a>
            <a
              v-if="f.contextSnapshot?.length"
              class="text-caption annotations__action"
              @click.prevent="contextId = contextId === f._id ? null : f._id"
            >
              {{ contextId === f._id ? 'Hide context' : 'Show context' }}
            </a>
          </div>

          <!--
            Inline conversation context: the annotation's durable snapshot
            (the turns leading up to the annotated reply, PII-redacted) —
            readable here even after the source conversation's 90-day TTL. 
          -->
          <div
            v-if="contextId === f._id && f.contextSnapshot?.length"
            class="annotations__context mt-2"
          >
            <div
              v-for="(turn, i) in f.contextSnapshot"
              :key="i"
              class="annotations__turn"
              :class="`annotations__turn--${turn.role}`"
            >
              <strong>{{ turn.role === 'user' ? 'Member' : 'RM AI' }}:</strong>
              {{ displayText(turn.content) }}
            </div>
          </div>
        </VCard>

        <VPagination
          v-if="store.feedbackPages > 1"
          :model-value="currentPage"
          :length="store.feedbackPages"
          :total-visible="5"
          class="mt-4"
          @update:model-value="onPageChange"
        />
      </div>
    </VCard>

    <!-- Full-conversation dialog (in place — no page hop) -->
    <VDialog
      :model-value="convDialogOpen"
      max-width="680"
      scrollable
      @update:model-value="val => { if (!val) closeConversation() }"
    >
      <VCard>
        <VCardTitle class="d-flex align-center justify-space-between pa-4">
          <span class="text-h6">{{ store.activeConversation?.title || 'Conversation' }}</span>
          <div class="d-flex align-center ga-1">
            <RouterLink
              v-if="convDialogFeedback"
              :to="`/admin-agent-conversations?conversationId=${convDialogFeedback.conversationId}`"
              class="text-caption mr-2"
              title="Open in the full viewer (traces, annotating other replies)"
            >
              Open in viewer ↗
            </RouterLink>
            <VBtn
              icon="mdi-close"
              size="small"
              variant="text"
              @click="closeConversation"
            />
          </div>
        </VCardTitle>
        <VDivider />
        <VCardText class="pa-4">
          <div
            v-if="store.activeConversationLoading"
            class="text-center py-8"
          >
            <VProgressCircular
              indeterminate
              color="primary"
            />
          </div>

          <!-- Live thread -->
          <template v-else-if="store.activeConversation?.messages?.length">
            <div
              v-for="(msg, i) in store.activeConversation.messages"
              :key="i"
              class="annotations__turn"
              :class="[
                `annotations__turn--${msg.role}`,
                { 'annotations__turn--annotated': convDialogFeedback && msg.msgDbIndex === convDialogFeedback.messageIndex },
              ]"
            >
              <strong>{{ msg.role === 'user' ? 'Member' : 'RM AI' }}:</strong>
              {{ displayText(msg.content) }}
              <span
                v-if="convDialogFeedback && msg.msgDbIndex === convDialogFeedback.messageIndex"
                class="annotations__annotated-tag"
              >⬅ annotated reply</span>
            </div>
          </template>

          <!-- Expired / unavailable → durable snapshot fallback -->
          <template v-else>
            <div class="text-body-2 text-medium-emphasis mb-3">
              The live conversation is no longer available (conversations expire after 90 days).
              Showing the annotation's saved snapshot instead:
            </div>
            <div
              v-for="(turn, i) in (convDialogFeedback?.contextSnapshot || [])"
              :key="i"
              class="annotations__turn"
              :class="`annotations__turn--${turn.role}`"
            >
              <strong>{{ turn.role === 'user' ? 'Member' : 'RM AI' }}:</strong>
              {{ displayText(turn.content) }}
            </div>
          </template>
        </VCardText>
      </VCard>
    </VDialog>
  </div>
</template>

<style lang="scss" scoped>
.annotations__ai-text {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.annotations__action {
  cursor: pointer;
  text-decoration: underline;
}

.annotations__context {
  border-radius: 8px;
  background: rgba(var(--v-theme-on-surface), 0.03);
  padding: 10px 12px;
  max-height: 340px;
  overflow-y: auto;
}

.annotations__turn {
  font-size: 13px;
  line-height: 1.45;
  padding: 4px 8px;
  margin-bottom: 4px;
  border-radius: 6px;
  white-space: pre-wrap;
}

.annotations__turn--user {
  background: rgba(var(--v-theme-primary), 0.07);
}

.annotations__turn--assistant {
  background: rgba(var(--v-theme-on-surface), 0.04);
}

.annotations__turn--annotated {
  border: 1.5px solid rgb(var(--v-theme-warning));
}

.annotations__annotated-tag {
  display: inline-block;
  margin-left: 6px;
  font-size: 11px;
  font-weight: 700;
  color: rgb(var(--v-theme-warning));
  white-space: nowrap;
}

// iOS zoom guard: inputs must render at >= 16px.
.annotations__input :deep(input),
.annotations__input :deep(.v-field__input) {
  font-size: 16px;
}
</style>

<route lang="yaml">
meta:
  layout: website
  action: read
  subject: AclDemo
  allowLoggedInOnly: true
</route>
